/**
 * ContractTemplates – Studio tab for managing contract templates
 * Custom rich text editor using contentEditable (React 19 compatible)
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  FileText,
  Trash2,
  Pencil,
  Loader2,
  Copy,
  Briefcase,
  User,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  Heading1,
  Heading2,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Strikethrough,
} from "lucide-react";

const VARIABLES = [
  { key: "{candidate_name}", label: "Candidate Name" },
  { key: "{candidate_email}", label: "Candidate Email" },
  { key: "{candidate_phone}", label: "Candidate Phone" },
  { key: "{candidate_address}", label: "Candidate Address" },
  { key: "{studio_name}", label: "Studio Name" },
  { key: "{studio_email}", label: "Studio Email" },
  { key: "{studio_phone}", label: "Studio Phone" },
  { key: "{job_title}", label: "Job Title" },
  { key: "{job_date}", label: "Job Date" },
  { key: "{job_time}", label: "Job Time" },
  { key: "{job_duration}", label: "Job Duration" },
  { key: "{pay_rate}", label: "Pay Rate" },
  { key: "{final_rate}", label: "Final Agreed Rate" },
  { key: "{effective_date}", label: "Effective Date" },
  { key: "{todays_date}", label: "Today's Date" },
];

/* ── Minimal Rich Text Editor ──────────────────────────────────── */
function RichEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value || "";
      isInitialized.current = true;
    }
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== value) {
      // Only reset if the value was cleared externally (e.g. new dialog)
      if (value === "" || value === undefined) {
        editorRef.current.innerHTML = "";
        isInitialized.current = false;
      } else if (!isInitialized.current) {
        editorRef.current.innerHTML = value;
        isInitialized.current = true;
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
  };

  const insertHTML = (html) => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, html);
    handleInput();
  };

  const addLink = () => {
    const url = prompt("Enter URL:");
    if (url) exec("createLink", url);
  };

  const ToolBtn = ({ onClick, icon: Icon, title, label }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="p-1.5 rounded text-white/50 hover:text-[#FF6B6B] hover:bg-white/5 transition-colors"
    >
      {Icon ? <Icon className="w-4 h-4" /> : <span className="text-xs font-bold">{label}</span>}
    </button>
  );

  return (
    <div className="border border-white/10 rounded overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-white/[0.03] border-b border-white/10">
        <ToolBtn onClick={() => exec("undo")} icon={Undo2} title="Undo" />
        <ToolBtn onClick={() => exec("redo")} icon={Redo2} title="Redo" />
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={() => exec("formatBlock", "h1")} icon={Heading1} title="Heading 1" />
        <ToolBtn onClick={() => exec("formatBlock", "h2")} icon={Heading2} title="Heading 2" />
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={() => exec("bold")} icon={Bold} title="Bold" />
        <ToolBtn onClick={() => exec("italic")} icon={Italic} title="Italic" />
        <ToolBtn onClick={() => exec("underline")} icon={Underline} title="Underline" />
        <ToolBtn onClick={() => exec("strikeThrough")} icon={Strikethrough} title="Strikethrough" />
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={() => exec("insertUnorderedList")} icon={List} title="Bullet List" />
        <ToolBtn onClick={() => exec("insertOrderedList")} icon={ListOrdered} title="Numbered List" />
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={() => exec("justifyLeft")} icon={AlignLeft} title="Align Left" />
        <ToolBtn onClick={() => exec("justifyCenter")} icon={AlignCenter} title="Center" />
        <div className="w-px h-5 bg-white/10 mx-1" />
        <ToolBtn onClick={addLink} icon={LinkIcon} title="Insert Link" />
      </div>
      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={(e) => {
          e.preventDefault();
          const html = e.clipboardData.getData("text/html");
          const text = e.clipboardData.getData("text/plain");
          if (html) {
            // Parse HTML, strip color/background styles but keep structure (bold, underline, etc.)
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            doc.querySelectorAll("*").forEach((el) => {
              el.style.removeProperty("color");
              el.style.removeProperty("background-color");
              el.style.removeProperty("background");
              el.style.removeProperty("font-family");
              el.style.removeProperty("font-size");
            });
            document.execCommand("insertHTML", false, doc.body.innerHTML);
          } else {
            document.execCommand("insertText", false, text);
          }
          handleInput();
        }}
        className="min-h-[250px] p-4 text-sm bg-white text-black outline-none contract-editor-content [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-1 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-2"
        data-placeholder={placeholder || "Write your contract here..."}
        data-testid="rich-editor"
        style={{ wordWrap: "break-word", caretColor: "black" }}
      />
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────── */
export default function ContractTemplates() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [contractType, setContractType] = useState("per_job");

  // Key to force re-mount of RichEditor
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/contracts/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setTitle("");
    setContent("");
    setContractType("per_job");
    setEditorKey((k) => k + 1);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setTitle(t.title);
    setContent(t.content);
    setContractType(t.contract_type);
    setEditorKey((k) => k + 1);
    setDialogOpen(true);
  };

  const insertVariable = (varKey) => {
    // Insert at cursor in the contentEditable
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      // Check if selection is inside the editor
      const editorEl = document.querySelector('[data-testid="rich-editor"]');
      if (editorEl && editorEl.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        const span = document.createElement("span");
        span.style.color = "#FF6B6B";
        span.style.fontWeight = "600";
        span.textContent = varKey;
        range.insertNode(span);
        // Move cursor after inserted node
        range.setStartAfter(span);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        // Trigger onChange
        if (editorEl) {
          setContent(editorEl.innerHTML);
        }
        return;
      }
    }
    // Fallback: append to the end
    setContent((prev) => prev + `<span style="color:#FF6B6B;font-weight:600">${varKey}</span>`);
    setEditorKey((k) => k + 1);
  };

  const handleSave = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (
      !content.trim() ||
      content === "<br>" ||
      content === "<div><br></div>"
    )
      return toast.error("Contract content is required");

    setSaving(true);
    try {
      const url = editing
        ? `${API}/contracts/templates/${editing.template_id}`
        : `${API}/contracts/templates`;
      const method = editing ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          content,
          contract_type: contractType,
        }),
      });

      if (res.ok) {
        toast.success(editing ? "Template updated" : "Template created");
        setDialogOpen(false);
        fetchTemplates();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save template");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (
      !window.confirm(
        "Delete this contract template? This cannot be undone."
      )
    )
      return;
    try {
      const res = await fetch(`${API}/contracts/templates/${templateId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success("Template deleted");
        fetchTemplates();
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contract-templates-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Contract Templates</h2>
          <p className="text-white/50 text-sm mt-1">
            Create contracts that candidates sign when you hire them.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-[#FF6B6B] hover:bg-[#FF5252] text-white"
          data-testid="create-template-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card className="bg-white/[0.03] border-white/10 p-8 text-center">
          <FileText className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/50">No contract templates yet.</p>
          <p className="text-white/30 text-sm mt-1">
            Create a template and it will be available when you hire
            candidates.
          </p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {templates.map((t) => (
            <Card
              key={t.template_id}
              className="bg-white/[0.03] border-white/10 p-5 flex items-center justify-between"
              data-testid={`template-card-${t.template_id}`}
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded bg-[#FF6B6B]/10 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-[#FF6B6B]" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{t.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        t.contract_type === "per_job"
                          ? "bg-blue-500/20 text-blue-300"
                          : "bg-purple-500/20 text-purple-300"
                      }`}
                    >
                      {t.contract_type === "per_job" ? (
                        <>
                          <Briefcase className="w-3 h-3 inline mr-1" />
                          Per Job
                        </>
                      ) : (
                        <>
                          <User className="w-3 h-3 inline mr-1" />
                          Per Person
                        </>
                      )}
                    </span>
                    <span className="text-xs text-white/30">
                      {new Date(t.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-4">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openEdit(t)}
                  className="text-white/60 hover:text-white hover:bg-white/10"
                  data-testid={`edit-template-${t.template_id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(t.template_id)}
                  className="text-white/60 hover:text-red-400 hover:bg-red-400/10"
                  data-testid={`delete-template-${t.template_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editing ? "Edit Template" : "New Contract Template"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Template Name
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Second Shooter Agreement"
                className="bg-white/5 border-white/10 text-white"
                data-testid="template-title-input"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Contract Type
              </label>
              <Select value={contractType} onValueChange={setContractType}>
                <SelectTrigger
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="contract-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                  <SelectItem value="per_job">
                    <span className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-blue-400" /> Per Job —
                      new contract for each hire
                    </span>
                  </SelectItem>
                  <SelectItem value="per_person">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4 text-purple-400" /> Per Person —
                      one contract applies to all future jobs
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Variables */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Insert Variable
              </label>
              <p className="text-white/40 text-xs mb-2">
                Click a variable to insert it at cursor. It will be auto-filled
                with real data when the contract is generated.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {VARIABLES.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    className="text-xs px-2.5 py-1 rounded bg-white/5 border border-white/10 text-white/70 hover:bg-[#FF6B6B]/20 hover:text-[#FF6B6B] hover:border-[#FF6B6B]/30 transition-colors"
                    data-testid={`var-btn-${v.key}`}
                  >
                    <Copy className="w-3 h-3 inline mr-1" />
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Contract Content
              </label>
              <RichEditor
                key={editorKey}
                value={content}
                onChange={setContent}
                placeholder="Write your contract here..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-bold"
                data-testid="save-template-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {editing ? "Update Template" : "Create Template"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
